/* eslint-disable no-param-reassign */
import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import HttpStatus from "http-status-codes";
import log from "loglevel";
import qs from "qs";
import { CancelledError } from "react-query";

import config from "@/config";
import { handleDatesString, handleStringDates } from "@/core/date";
import i18n from "@/i18n";
import { CommonPagedRequestParams, TypedRequestConfig } from "@/shared/domain";
import {
    ForbiddenError,
    GoneError,
    NetworkError,
    NotFoundError,
    RequestError,
    TimeError,
    TimeoutError,
    TokenExpiredError,
    UnauthorizedError,
} from "@/shared/errors";
import { pi2AuthTokenProvider, tokenProvider, xCorrelationIdProvider } from "@/shared/hooks/useAuth/providers";

let abortController = new AbortController();

export const cancelRequests = (): void => {
    abortController.abort();
    abortController = new AbortController();
};

export const requestConfig = <Type>(options: TypedRequestConfig<Type> = {}): TypedRequestConfig<Type> => options;

export const pagedRequestConfig = <Type extends CommonPagedRequestParams>(
    options: TypedRequestConfig<Type> = {},
): TypedRequestConfig<Type> => {
    const { params, ...others } = options;

    return requestConfig<Type>({
        ...others,
        params: {
            ...params,
            pageNumber: params?.pageNumber || 1,
            pageSize: params?.pageSize || config.DEFAULT_PAGE_SIZE,
        },
    });
};

function handleError(baseError: AxiosError) {
    log.warn(`API Error on '${baseError.config?.url}': `, baseError.message);

    if (baseError instanceof TokenExpiredError) {
        cancelRequests();
        throw baseError;
    }

    // No connection, DNS error, cert error, ...
    if (!baseError.response) {
        if (baseError.code === "ERR_CANCELED") {
            throw new CancelledError();
        }

        let networkError = new NetworkError();
        if (baseError.message.includes("timeout")) {
            networkError = new TimeoutError(baseError.message);
        }

        throw networkError;
    }

    // fix invalid 400 when user/passw is incorrect
    // prettier-ignore
    if (baseError.config.url.toLowerCase().includes("user/authenticate") && baseError.response?.status === HttpStatus.BAD_REQUEST) {
        baseError.response.status = HttpStatus.UNAUTHORIZED; // returned by API as 400 error
    }

    // Error response from server
    let requestError = new RequestError(baseError.response.status, baseError.response.data);
    if (baseError.response.status === HttpStatus.FORBIDDEN) {
        if ((baseError.response.data as MyBrokerApi.ApiError)?.message?.includes?.("local time")) {
            requestError = new TimeError(baseError.response.data);
        } else {
            requestError = new ForbiddenError(baseError.response.data);
        }
    }

    if (baseError.response.status === HttpStatus.UNAUTHORIZED) {
        requestError = new UnauthorizedError(baseError.response.data);
    }

    if (baseError.response.status === HttpStatus.NOT_FOUND) {
        requestError = new NotFoundError(baseError.response.data);
    }

    if (baseError.response.status === HttpStatus.GONE) {
        requestError = new GoneError(baseError.response.data);
    }

    if (requestError.status === HttpStatus.CONFLICT || requestError.status === HttpStatus.PRECONDITION_FAILED) {
        // business error, component should handle it by default
        throw requestError;
    }

    throw requestError;
}

// Handle response data
function handleData(originalResponse: AxiosResponse<any, any>) {
    handleStringDates(originalResponse.data);
    return originalResponse;
}

//
// IDP API
//

const idpApi = axios.create({
    baseURL: config.IDP_URL,
    headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
    },
    timeout: config.REQUEST_TIMEOUT * 1000,
});

let responseInterceptorIdpApi;
let requestInterceptorIdpApi;

export function getIdpApi(): AxiosInstance {
    idpApi.interceptors.response.eject(responseInterceptorIdpApi);
    idpApi.interceptors.request.eject(requestInterceptorIdpApi);

    responseInterceptorIdpApi = idpApi.interceptors.response.use(handleData, handleError);

    requestInterceptorIdpApi = idpApi.interceptors.request.use((axiosRequestConfig) => {
        const accessToken = pi2AuthTokenProvider.getToken();
        if (accessToken && !axiosRequestConfig.headers.Authorization) {
            axiosRequestConfig.headers.Authorization = `Bearer ${accessToken}`;
        }

        axiosRequestConfig.signal = abortController.signal;

        return axiosRequestConfig;
    });

    return idpApi;
}

//
// SP API
//
export const spApi = axios.create({
    baseURL: `${config.SPAPI_URL}/api/`,
    headers: {
        Accept: "application/json;application/mybroker",
        // "Cache-Control": "no-cache; no-store; private",
        "x-version": "1.0",
    },
    timeout: config.REQUEST_TIMEOUT * 1000,
    paramsSerializer: (params) => {
        return qs.stringify(params, {
            arrayFormat: "comma",
            serializeDate: (d) => d.toBeDateFormat(),
        });
    },
    transformRequest: [
        (data) => {
            return handleDatesString(data);
        },
        axios.defaults.transformRequest[0],
    ],
});

let responseInterceptorSpApi;
let requestInterceptorSpApi;

export function getSpApi(mock = false): AxiosInstance {
    const accessToken = tokenProvider.getToken();

    spApi.interceptors.response.eject(responseInterceptorSpApi);
    spApi.interceptors.request.eject(requestInterceptorSpApi);

    responseInterceptorSpApi = spApi.interceptors.response.use(handleData, handleError);

    requestInterceptorSpApi = spApi.interceptors.request.use((axiosRequestConfig) => {
        if (accessToken && !axiosRequestConfig.headers.Authorization) {
            axiosRequestConfig.headers.Authorization = `Bearer ${accessToken}`;
        }

        axiosRequestConfig.headers["Accept-Language"] = i18n.language || config.FALLBACK_LANGUAGE;
        axiosRequestConfig.headers["X-Correlation-Id"] = xCorrelationIdProvider.getXCorrelationId();
        if (mock) {
            axiosRequestConfig.headers["X-Mock-Data"] = "1";
        }

        axiosRequestConfig.signal = abortController.signal;

        return axiosRequestConfig;
    });

    return spApi;
}

//
// Files API
//

const filesApi = axios.create({
    headers: {
        "x-version": "1.0",
    },
    timeout: config.REQUEST_TIMEOUT * 1000,
});

let requestInterceptorFilesApi;

export function getFilesApi(mock = false): AxiosInstance {
    const accessToken = tokenProvider.getToken();

    filesApi.interceptors.request.eject(requestInterceptorFilesApi);

    requestInterceptorFilesApi = filesApi.interceptors.request.use((axiosRequestConfig) => {
        if (accessToken && !axiosRequestConfig.headers.Authorization) {
            axiosRequestConfig.headers.Authorization = `Bearer ${accessToken}`;
        }

        axiosRequestConfig.headers["Accept-Language"] = i18n.language || "en";
        axiosRequestConfig.headers["X-Correlation-Id"] = xCorrelationIdProvider.getXCorrelationId();
        if (mock) {
            axiosRequestConfig.headers["X-Mock-Data"] = "1";
        }

        axiosRequestConfig.signal = abortController.signal;

        return axiosRequestConfig;
    });

    return filesApi;
}

export const mapToFormData = <FormType extends object>(formValues: FormType): FormData =>
    Object.keys(formValues).reduce((formData, key) => {
        if (Array.isArray(formValues[key]) && formValues[key][0] instanceof File) {
            formValues[key].forEach((document) => formData.append(key, document));
        } else if (formValues[key] instanceof Date) {
            formData.append(key, JSON.stringify(formValues[key].toBeDateFormat()));
        } else if (formValues[key] instanceof Object) {
            formData.append(key, JSON.stringify(handleDatesString(formValues[key])));
        } else {
            formData.append(key, formValues[key]);
        }
        return formData;
    }, new FormData());

const externalApi = axios.create({
    timeout: config.REQUEST_TIMEOUT * 1000,
});

export function getExternalApi(): AxiosInstance {
    externalApi.interceptors.request.use((axiosRequestConfig) => {
        axiosRequestConfig.headers["Accept-Language"] = i18n.language || "en";
        return axiosRequestConfig;
    });
    return externalApi;
}
